import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Center, Spinner, Text } from '@chakra-ui/react';
import { SetupStep } from '../types';
import SetupLayout from '../components/setup/SetupLayout';
import TargetHoursStep from '../components/setup/TargetHoursStep';
import TrackingPeriodStep from '../components/setup/TrackingPeriodStep';
import WorkWeekStep from '../components/setup/WorkWeekStep';
import VacationDaysStep from '../components/setup/VacationDaysStep';
import { useOptimizedUserStore as useUserStore } from '../store/optimizedUserStore';
import { useAuthStore } from '../store/authStore';
import { format } from 'date-fns';
import { supabase } from '../services/supabase';

// Define the component as a const variable with an arrow function
const SetupWizard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut } = useAuthStore();
  const { 
    profile, 
    fetchUserProfile, 
    updateProfile, 
    addVacationDay,
    profileLoading 
  } = useUserStore();
  
  const [currentStep, setCurrentStep] = useState<SetupStep>(SetupStep.TARGET_HOURS);
  const [yearlyTargetHours, setYearlyTargetHours] = useState(1800);
  const [trackingStartDate, setTrackingStartDate] = useState<Date>(
    new Date(new Date().getFullYear(), 0, 1) // January 1st of current year
  );
  const [trackingEndDate, setTrackingEndDate] = useState<Date>(
    new Date(new Date().getFullYear(), 11, 31) // December 31st of current year
  );
  const [workWeekDays, setWorkWeekDays] = useState<number[]>([1, 2, 3, 4, 5]); // Mon-Fri by default
  const [vacationDays, setVacationDays] = useState<Date[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasLoggedConfirmationMessage, setHasLoggedConfirmationMessage] = useState(false);

  // Define titles and subtitles for each step
  const stepDetails = [
    { title: 'Annual Target Hours', subtitle: 'Set your yearly billable hours goal.' },
    { title: 'Tracking Period', subtitle: 'Define the start and end dates for tracking.' },
    { title: 'Work Week', subtitle: 'Select the days you typically work.' },
    { title: 'Vacation Days', subtitle: 'Mark any planned vacation days.' },
  ];

  // Listen for email verification events from other tabs
  useEffect(() => {
    // Set up BroadcastChannel to listen for verification events
    let authChannel: BroadcastChannel | null = null;
    
    const handleAuthEvent = async (event: MessageEvent) => {
      if (event.data?.type === 'EMAIL_VERIFIED') {
        console.log('Received email verification message from another tab');
        // Force refresh the auth session
        const { data } = await supabase.auth.getSession();
        if (data && data.session) {
          console.log('Session refreshed after email verification');
          // Force a refresh of the auth state
          if (typeof fetchUserProfile === 'function') {
            await fetchUserProfile(true);
          }
          // Reload the page to ensure we have the latest auth state
          window.location.reload();
        }
      }
    };
    
    try {
      authChannel = new BroadcastChannel('auth_channel');
      authChannel.onmessage = handleAuthEvent;
    } catch (err) {
      console.warn('BroadcastChannel not supported in this browser', err);
    }
    
    // Check if email was verified in another tab via localStorage
    const emailVerified = localStorage.getItem('emailVerificationComplete') === 'true';
    if (emailVerified && user && !user.email_confirmed_at) {
      console.log('Email verified in another tab, refreshing session');
      supabase.auth.refreshSession().then(() => {
        window.location.reload();
      });
    }
    
    return () => {
      // Clean up listener
      if (authChannel) {
        authChannel.close();
      }
    };
  }, [user, fetchUserProfile]);

  // Check if user is authenticated and handle profile loading
  useEffect(() => {
    // Only attempt to fetch the profile if the user is confirmed
    if (user && user.email_confirmed_at && !profile && !profileLoading) {
      console.log('User confirmed, fetching user profile in SetupWizard');
      fetchUserProfile(); // Removed boolean argument
    } else if (user && !user.email_confirmed_at && !hasLoggedConfirmationMessage) {
      console.log('User email not confirmed yet. Please check your email to complete registration.');
      setHasLoggedConfirmationMessage(true);
      
      // Check if we need to refresh the session
      const emailVerified = localStorage.getItem('emailVerificationComplete') === 'true';
      if (emailVerified) {
        console.log('Email verified according to localStorage, refreshing session');
        supabase.auth.refreshSession().then(() => {
          window.location.reload();
        });
      }
    }
    
    // Initialize form with profile data if available and not already initialized
    if (profile && !isInitialized && !profile.setup_completed) { // Added check for !profile.setup_completed
      console.log("SetupWizard useEffect: Initializing form with existing profile data.");
      setYearlyTargetHours(profile.yearly_target_hours || 1800);
      
      // Set tracking dates if they exist in the profile
      if (profile.tracking_start_date) {
        // Ensure date parsing handles potential timezone issues by treating as UTC
        const [year, month, day] = profile.tracking_start_date.split('-').map(Number);
        setTrackingStartDate(new Date(Date.UTC(year, month - 1, day)));
      }
      
      if (profile.tracking_end_date) {
        const [year, month, day] = profile.tracking_end_date.split('-').map(Number);
        setTrackingEndDate(new Date(Date.UTC(year, month - 1, day)));
      }
      
      // Set work week days
      if (profile.work_week_days && profile.work_week_days.length > 0) {
        setWorkWeekDays(profile.work_week_days);
      }
      
      setIsInitialized(true);
    }
  }, [user, profile, profileLoading, fetchUserProfile, navigate, isInitialized, hasLoggedConfirmationMessage]);
  
  // Show loading state while waiting for auth and profile
  if (authLoading || (user && profileLoading)) {
    return (
      <Center h="100vh" flexDirection="column">
        <Spinner size="xl" mb={4} />
        <Text>Setting up your account...</Text>
      </Center>
    );
  }
  
  const handleNext = () => {
    if (currentStep < SetupStep.COMPLETE - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleComplete = async () => {
    try {
      // Format dates to ISO strings (YYYY-MM-DD)
      const formattedStartDate = format(trackingStartDate, 'yyyy-MM-dd');
      const formattedEndDate = format(trackingEndDate, 'yyyy-MM-dd');

      console.log('Updating user profile with the following data:', {
        yearly_target_hours: yearlyTargetHours,
        tracking_start_date: formattedStartDate,
        tracking_end_date: formattedEndDate,
        work_week_days: workWeekDays,
        setup_completed: true
      });

      // Update the profile
      await updateProfile({
        yearly_target_hours: yearlyTargetHours,
        tracking_start_date: formattedStartDate,
        tracking_end_date: formattedEndDate,
        work_week_days: workWeekDays,
        setup_completed: true  // Mark setup as completed
      });

      // Add vacation days
      if (vacationDays.length > 0) {
        for (const date of vacationDays) {
          const formattedDate = format(date, 'yyyy-MM-dd');
          await addVacationDay(formattedDate);
        }
      }

      // Force a manual fetch from the database to ensure the store has the latest data
      console.log('Refreshing user profile state after setup completion.');
      await fetchUserProfile(true); // Force refresh to update the store
      
      // The issue may be related to React Router not responding to the profile update
      // Use window.location.href to force a full page navigation instead of React Router
      console.log('Setup completed successfully, redirecting to dashboard.');
      window.location.href = '/dashboard';

    } catch (error) {
      console.error('Error saving profile:', error);
      // Consider adding user feedback here, e.g., using a toast notification
    }
  };

  // Handle sign out and redirect to login
  const handleSignOut = async () => {
    try {
      console.log('Signing out user from setup wizard');
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  // Render the current step
  const renderStep = () => {
    switch (currentStep) {
      case SetupStep.TARGET_HOURS:
        return (
          <TargetHoursStep
            value={yearlyTargetHours}
            onChange={setYearlyTargetHours}
            onNext={handleNext} // Pass handleNext
          />
        );
      case SetupStep.DATE_RANGE: // Corrected enum name
        return (
          <TrackingPeriodStep
            startDate={trackingStartDate}
            endDate={trackingEndDate}
            onStartDateChange={setTrackingStartDate}
            onEndDateChange={setTrackingEndDate}
            onNext={handleNext} // Pass handleNext
            onBack={handleBack} // Pass handleBack
          />
        );
      case SetupStep.WORK_WEEK:
        return (
          <WorkWeekStep
            selectedDays={workWeekDays}
            onChange={setWorkWeekDays}
            onNext={handleNext} // Pass handleNext
            onBack={handleBack} // Pass handleBack
          />
        );
      case SetupStep.VACATION_DAYS:
        return (
          <VacationDaysStep
            selectedDates={vacationDays}
            onChange={setVacationDays}
            startDate={trackingStartDate} // Pass required props
            endDate={trackingEndDate}     // Pass required props
            workWeekDays={workWeekDays} // Pass required props
            onComplete={handleComplete} // Pass handleComplete
            onBack={handleBack}       // Pass handleBack
          />
        );
      default:
        return null;
    }
  };

  return (
    <SetupLayout
      currentStep={currentStep}
      // Pass title and subtitle based on current step
      title={stepDetails[currentStep]?.title || 'Setup'}
      subtitle={stepDetails[currentStep]?.subtitle}
      onSignOut={handleSignOut}
      userEmail={user?.email} // Add user email
    >
      {renderStep()}
    </SetupLayout>
  );
};

// Export the component
export default SetupWizard;
